import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Cloud,
  Loader2,
  CheckCircle,
  XCircle,
  Cpu,
  HardDrive,
  MemoryStick,
} from "lucide-react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { checkProvisioningStatus, checkSubdomainAvailability } from "@/data/automation-server";
import {
  useHetznerOptions,
  useProvisionHetznerServer,
} from "@/hooks/useAutomationServersQuery";
import type { HetznerServerConfig, HetznerRegion } from "@/data/automation-server";

interface ProvisionHetznerServerModalProps {
  children?: React.ReactNode;
  onServerCreated?: (serverId: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const PROVISIONING_STEPS = [
  { key: "pending", label: "Preparing..." },
  { key: "creating_server", label: "Creating server" },
  { key: "waiting_for_server", label: "Waiting for server to start" },
  { key: "creating_dns", label: "Configuring DNS" },
  { key: "installing_software", label: "Installing software" },
  { key: "registering", label: "Registering server" },
  { key: "archiving_ssh_key", label: "Archiving SSH key" },
  { key: "completed", label: "Completed" },
];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);
}

export function ProvisionHetznerServerModal({
  children,
  onServerCreated,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: ProvisionHetznerServerModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpenState = controlledOnOpenChange || setInternalOpen;
  const [step, setStep] = useState<"configure" | "provisioning" | "complete">(
    "configure"
  );

  // Form state
  const [serverName, setServerName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [subdomainManuallyEdited, setSubdomainManuallyEdited] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Subdomain availability state
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);
  const [subdomainCheckError, setSubdomainCheckError] = useState<string | null>(null);
  const [isCheckingSubdomain, setIsCheckingSubdomain] = useState(false);

  // Provisioning state
  const [automationServerId, setAutomationServerId] = useState<string | null>(
    null
  );
  const [provisioningStatus, setProvisioningStatus] = useState<string | null>(
    null
  );
  const [provisioningError, setProvisioningError] = useState<string | null>(
    null
  );
  const [provisionedDomain, setProvisionedDomain] = useState<
    string | null
  >(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { data: hetznerOptions, isLoading: optionsLoading, refetch: refetchOptions } =
    useHetznerOptions();
  const { mutate: provision, isPending: isProvisioning } =
    useProvisionHetznerServer();

  // Refetch options each time the modal opens to get fresh active/inactive state
  useEffect(() => {
    if (open) {
      refetchOptions();
    }
  }, [open, refetchOptions]);

  // Filter configs to only those available in the selected region
  const availableConfigs = hetznerOptions?.configs.filter((config) => {
    if (!selectedRegion) return true;
    if (!config.available_locations?.length) return false;
    return config.available_locations.includes(selectedRegion);
  });

  const selectedConfig = availableConfigs?.find(
    (c) => c.id === selectedConfigId
  );

  // Auto-fill subdomain from server name
  useEffect(() => {
    if (!subdomainManuallyEdited && serverName) {
      setSubdomain(slugify(serverName));
    }
  }, [serverName, subdomainManuallyEdited]);

  // Debounced subdomain availability check
  useEffect(() => {
    if (!subdomain) {
      setSubdomainAvailable(null);
      setSubdomainCheckError(null);
      return;
    }

    setSubdomainAvailable(null);
    setSubdomainCheckError(null);
    setIsCheckingSubdomain(true);

    const timeout = setTimeout(async () => {
      try {
        const result = await checkSubdomainAvailability(subdomain);
        setSubdomainAvailable(result.available);
        setSubdomainCheckError(result.error || null);
      } catch {
        setSubdomainCheckError("Could not check availability");
      } finally {
        setIsCheckingSubdomain(false);
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
      setIsCheckingSubdomain(false);
    };
  }, [subdomain]);

  // Clear config selection when region changes if config is no longer available
  useEffect(() => {
    if (selectedConfigId && availableConfigs) {
      const stillValid = availableConfigs.some((c) => c.id === selectedConfigId);
      if (!stillValid) {
        setSelectedConfigId("");
      }
    }
  }, [selectedRegion]);

  // Poll provisioning status
  const pollStatus = useCallback(async () => {
    if (!automationServerId) return;
    try {
      const data = await checkProvisioningStatus(automationServerId);
      setProvisioningStatus(data.provisioning_status);
      setProvisioningError(data.provisioning_error);
      if (data.domain) setProvisionedDomain(data.domain);

      if (data.provisioning_status === "completed") {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setStep("complete");
        // Auto-close after 3 seconds
        setTimeout(() => {
          handleOpenChange(false);
          if (onServerCreated && automationServerId) {
            onServerCreated(automationServerId);
          }
        }, 3000);
      } else if (data.provisioning_status === "failed") {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch (err) {
      console.error("Failed to check provisioning status:", err);
    }
  }, [automationServerId, onServerCreated]);

  useEffect(() => {
    if (step === "provisioning" && automationServerId) {
      pollStatus();
      intervalRef.current = setInterval(pollStatus, 3000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [step, automationServerId, pollStatus]);

  const handleProvision = () => {
    if (!serverName.trim() || !subdomain || !selectedConfigId || !selectedRegion) {
      setError("All fields are required.");
      return;
    }
    setError(null);
    provision(
      {
        name: serverName.trim(),
        subdomain,
        server_config_id: selectedConfigId,
        region: selectedRegion,
      },
      {
        onSuccess: (data) => {
          setAutomationServerId(data.automation_server_id);
          setProvisioningStatus(data.provisioning_status);
          setProvisionedDomain(data.domain);
          setStep("provisioning");
        },
        onError: (err: any) => {
          setError(
            err.response?.data?.error ||
              err.message ||
              "Failed to start provisioning"
          );
        },
      }
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpenState(newOpen);
    if (!newOpen) {
      // Reset all state
      setStep("configure");
      setServerName("");
      setSubdomain("");
      setSubdomainManuallyEdited(false);
      setSubdomainAvailable(null);
      setSubdomainCheckError(null);
      setIsCheckingSubdomain(false);
      setSelectedConfigId("");
      setSelectedRegion("");
      setError(null);
      setAutomationServerId(null);
      setProvisioningStatus(null);
      setProvisioningError(null);
      setProvisionedDomain(null);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  const getStepIndex = (statusKey: string | null) => {
    if (!statusKey) return -1;
    return PROVISIONING_STEPS.findIndex((s) => s.key === statusKey);
  };

  const currentStepIndex = getStepIndex(provisioningStatus);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5" />
            {step === "configure"
              ? "Provision Cloud Server"
              : step === "provisioning"
              ? "Provisioning..."
              : "Server Ready"}
          </DialogTitle>
          <DialogDescription>
            {step === "configure"
              ? "Configure and provision a new Hetzner Cloud automation server."
              : step === "provisioning"
              ? "Your server is being set up. This may take a few minutes."
              : "Your automation server has been provisioned and is ready to use."}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Configuration */}
        {step === "configure" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Server Name</label>
              <Input
                placeholder="my-production-server"
                value={serverName}
                onChange={(e) => setServerName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Subdomain</label>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="my-server"
                  value={subdomain}
                  onChange={(e) => {
                    setSubdomain(slugify(e.target.value));
                    setSubdomainManuallyEdited(true);
                  }}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  .bswn.io
                </span>
              </div>
              {subdomain && (
                <div className="flex items-center gap-2 text-xs">
                  <code className="bg-muted px-1 rounded">
                    {subdomain}.bswn.io
                  </code>
                  {isCheckingSubdomain && (
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Checking...
                    </span>
                  )}
                  {!isCheckingSubdomain && subdomainAvailable === true && (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Available
                    </span>
                  )}
                  {!isCheckingSubdomain && subdomainAvailable === false && (
                    <span className="text-red-600 flex items-center gap-1">
                      <XCircle className="h-3 w-3" />
                      {subdomainCheckError || "Already in use"}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Region</label>
              {optionsLoading ? (
                <div className="flex items-center gap-2 p-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">
                    Loading server options...
                  </span>
                </div>
              ) : (
                <Select
                  value={selectedRegion}
                  onValueChange={setSelectedRegion}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a region" />
                  </SelectTrigger>
                  <SelectContent>
                    {hetznerOptions?.regions.map((region: HetznerRegion) => (
                      <SelectItem key={region.hetzner_location_id} value={region.hetzner_location_id}>
                        {region.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Server Size</label>
              <div className="grid gap-2">
                {availableConfigs?.map((config: HetznerServerConfig) => (
                  <button
                    key={config.id}
                    type="button"
                    onClick={() => setSelectedConfigId(config.id)}
                    className={`border rounded-lg p-3 text-left transition-all ${
                      selectedConfigId === config.id
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{config.display_name}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Cpu className="h-3 w-3" />
                            {config.vcpus} vCPUs
                          </span>
                          <span className="flex items-center gap-1">
                            <MemoryStick className="h-3 w-3" />
                            {config.memory_gb} GB RAM
                          </span>
                          <span className="flex items-center gap-1">
                            <HardDrive className="h-3 w-3" />
                            {config.disk_gb} GB
                          </span>
                        </div>
                      </div>
                      {config.monthly_price && (
                        <div className="text-right">
                          <p className="font-semibold">
                            {config.monthly_price} {config.currency}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            /month
                          </p>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {selectedConfig?.monthly_price && (
              <div className="rounded-md bg-blue-50 p-3">
                <p className="text-sm text-blue-800">
                  Monthly cost:{" "}
                  <strong>
                    {selectedConfig.monthly_price}{" "}
                    {selectedConfig.currency}
                  </strong>
                </p>
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <Button
              onClick={handleProvision}
              disabled={
                isProvisioning ||
                !serverName.trim() ||
                !subdomain ||
                !selectedConfigId ||
                !selectedRegion ||
                subdomainAvailable !== true
              }
              className="w-full"
            >
              {isProvisioning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Starting provisioning...
                </>
              ) : (
                "Provision Server"
              )}
            </Button>
          </div>
        )}

        {/* Step 2: Provisioning Progress */}
        {step === "provisioning" && (
          <div className="space-y-4">
            <div className="space-y-2">
              {PROVISIONING_STEPS.map((s, i) => {
                const isCompleted = i < currentStepIndex;
                const isCurrent = i === currentStepIndex;
                const isFailed =
                  provisioningStatus === "failed" && isCurrent;
                return (
                  <div
                    key={s.key}
                    className={`flex items-center gap-3 py-1.5 ${
                      isCompleted
                        ? "text-green-700"
                        : isCurrent
                        ? isFailed
                          ? "text-red-700"
                          : "text-blue-700"
                        : "text-muted-foreground"
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                    ) : isCurrent ? (
                      isFailed ? (
                        <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                      ) : (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-600 flex-shrink-0" />
                      )
                    ) : (
                      <div className="h-4 w-4 rounded-full border border-muted-foreground/30 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        isCurrent ? "font-medium" : ""
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {provisionedDomain &&
              currentStepIndex >= 3 && (
                <div className="rounded-md bg-green-50 p-3">
                  <p className="text-sm text-green-800">
                    Domain configured:{" "}
                    <code className="bg-green-100 px-1 rounded font-mono">
                      {provisionedDomain}
                    </code>
                  </p>
                </div>
              )}

            {provisioningStatus === "failed" && provisioningError && (
              <div className="rounded-md bg-red-50 p-3">
                <p className="text-sm text-red-800">
                  <strong>Error:</strong> {provisioningError}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Completion */}
        {step === "complete" && (
          <div className="space-y-4">
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <p className="text-sm font-medium text-green-800">
                  Server provisioned successfully!
                </p>
              </div>
              {provisionedDomain && (
                <p className="text-sm text-green-700">
                  Available at{" "}
                  <code className="bg-green-100 px-1 rounded font-mono">
                    {provisionedDomain}
                  </code>
                </p>
              )}
            </div>
            <p className="text-xs text-muted-foreground text-center">
              This dialog will close automatically...
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
